import { useEffect, useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { SupportLinks, type SupportLinkKind } from "./SupportLinks";
import {
  getProfileNameValidationError,
  MAX_PROFILE_NAME_LENGTH,
  sanitizeProfileName
} from "../game/profileStorage";
import type { Theme } from "../theme";

type SettingsModalProps = {
  buildLabel: string;
  discordUrl?: string;
  environmentLabel: string;
  feedbackUrl?: string;
  musicVolumePercent: number;
  sfxVolumePercent: number;
  open: boolean;
  onOpenLink?: (kind: SupportLinkKind, url: string) => void;
  onMusicVolumeChange: (musicVolumePercent: number) => void;
  onSfxVolumeChange: (sfxVolumePercent: number) => void;
  theme: Theme;
  profileName: string;
  closeLabel: string;
  onClose: () => void;
  onMainMenu?: () => void;
  onSaveProfileName: (profileName: string) => void;
  onViewRules?: () => void;
  onRestart?: () => void;
  supportEmail?: string;
};

export function SettingsModal({
  buildLabel,
  discordUrl,
  environmentLabel,
  feedbackUrl,
  musicVolumePercent,
  sfxVolumePercent,
  open,
  onOpenLink,
  onMusicVolumeChange,
  onSfxVolumeChange,
  theme,
  profileName,
  closeLabel,
  onClose,
  onMainMenu,
  onSaveProfileName,
  onViewRules,
  onRestart,
  supportEmail
}: SettingsModalProps) {
  const [draftProfileName, setDraftProfileName] = useState(profileName);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const requiresProfileName = sanitizeProfileName(profileName).length > 0;
  const validationError = getProfileNameValidationError(draftProfileName, {
    requireNonBlank: requiresProfileName
  });
  const displayedValidationError = hasAttemptedSave ? validationError : null;

  useEffect(() => {
    if (!open) {
      return;
    }

    setDraftProfileName(profileName);
    setHasAttemptedSave(false);
  }, [open, profileName]);

  if (!open) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (validationError !== null) {
      setHasAttemptedSave(true);
      return;
    }

    onSaveProfileName(draftProfileName);
    onClose();
  };

  const handleMusicVolumeInput = (event: FormEvent<HTMLInputElement>) => {
    onMusicVolumeChange(Number(event.currentTarget.value));
  };

  const handleSfxVolumeInput = (event: FormEvent<HTMLInputElement>) => {
    onSfxVolumeChange(Number(event.currentTarget.value));
  };

  return (
    <motion.div
      className="settings-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      onClick={onClose}
    >
      <motion.div
        className={`settings-card panel panel-${theme}`}
        initial={{ opacity: 0, scale: 0.94, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="settings-header">
          <h2 className="settings-title">Settings</h2>
          <button type="button" className="settings-close" onClick={onClose}>
            {closeLabel}
          </button>
        </div>

        <form className="settings-form" onSubmit={handleSubmit}>
          <label className="settings-field">
            <span className="settings-field-label">Profile Name</span>
            <input
              type="text"
              className="settings-input"
              value={draftProfileName}
              maxLength={MAX_PROFILE_NAME_LENGTH}
              aria-invalid={displayedValidationError !== null}
              placeholder="You"
              autoCapitalize="words"
              autoComplete="nickname"
              onChange={(event) => setDraftProfileName(sanitizeProfileName(event.target.value))}
            />
          </label>
          {displayedValidationError !== null ? (
            <p className="settings-helper settings-helper-error" role="alert">
              {displayedValidationError}
            </p>
          ) : null}

          <label className="settings-field">
            <div className="settings-range-header">
              <span className="settings-field-label">Music Volume</span>
              <span className="settings-range-value">{musicVolumePercent}%</span>
            </div>
            <input
              type="range"
              className="settings-range"
              min="0"
              max="100"
              step="1"
              value={musicVolumePercent}
              onInput={handleMusicVolumeInput}
            />
          </label>

          <label className="settings-field">
            <div className="settings-range-header">
              <span className="settings-field-label">SFX Volume</span>
              <span className="settings-range-value">{sfxVolumePercent}%</span>
            </div>
            <input
              type="range"
              className="settings-range"
              min="0"
              max="100"
              step="1"
              value={sfxVolumePercent}
              onInput={handleSfxVolumeInput}
            />
          </label>

          <div className="settings-actions">
            <button type="submit" className="settings-action settings-action-primary">
              Save Name
            </button>
            {onViewRules !== undefined ? (
              <button type="button" className="settings-action" onClick={onViewRules}>
                View Rules
              </button>
            ) : null}
            {onRestart !== undefined ? (
              <button type="button" className="settings-action" onClick={onRestart}>
                Restart
              </button>
            ) : null}
            {onMainMenu !== undefined ? (
              <button type="button" className="settings-action" onClick={onMainMenu}>
                Main Menu
              </button>
            ) : null}
          </div>
        </form>

        <SupportLinks
          className="settings-links"
          discordUrl={discordUrl}
          feedbackUrl={feedbackUrl}
          supportEmail={supportEmail}
          onOpenLink={onOpenLink}
        />

        <div className="settings-meta">
          <p className="settings-environment">{environmentLabel}</p>
          <p className="settings-build">{buildLabel}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}
